import { CloseOutlined } from '@ant-design/icons';
import { Alert, App, Button, Select, Spin } from 'antd';
import Form from 'antd/es/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useDepartments } from '@/common/useDepartments';
import { useAuth } from '@/core/providers/AuthFirebaseProvider';
import {
  canShowRecallTransition,
  filterStatusTransitionsHideRecallUnlessBlockedDriver,
  RECALL_STATUS,
  requiresDriver,
} from '@/core/utils/order_status_validation';
import {
  useGetOrderListByIdWithDriversWarehouses,
  useUpdateOrderFields,
} from '../../query/orders.hooks';
import { Export } from './components/Export';
import { layoutFormProps } from './layout';

const { Option } = Select;

interface OrderDrawerFormProps {
  orderIds: number[];
  close: () => void;
}

export const OrderDrawerMultiForm: FC<OrderDrawerFormProps> = ({ orderIds, close }) => {
  const { message } = App.useApp();
  const { userDataParams } = useAuth();
  const [isFormValid, setIsFormValid] = useState(false);

  const { selectedDepartmentId, departments } = useDepartments();
  const { data, isLoading, isFetching } = useGetOrderListByIdWithDriversWarehouses({
    ids: orderIds,
    departmentId: selectedDepartmentId,
  });

  const updateOrder = useUpdateOrderFields();

  const isManager = userDataParams?.roles.includes('manager');

  const canChangeStatus = Boolean(
    data?.order?.every((v) => v.status?.name === data?.order?.[0].status?.name)
  );

  const hasBlockedDriverOrders = useMemo(() => {
    if (!data?.order?.length) return false;
    return data.order.every(
      (order) => order.driver?.blocked && canShowRecallTransition(order.status?.name)
    );
  }, [data?.order]);

  const [hasDriverChangedInForm, setHasDriverChangedInForm] = useState(false);

  const visibleStatusTransitions = useMemo(
    () =>
      filterStatusTransitionsHideRecallUnlessBlockedDriver(
        data?.order?.[0]?.status?.status_transitions,
        hasBlockedDriverOrders,
        data?.order?.[0]?.status?.name
      ),
    [data?.order, hasBlockedDriverOrders]
  );

  const showRecallOption = useMemo(() => {
    if (!hasBlockedDriverOrders || hasDriverChangedInForm) return false;
    if (!canChangeStatus) return false;
    return !visibleStatusTransitions.some((t) => t.next === RECALL_STATUS);
  }, [hasBlockedDriverOrders, hasDriverChangedInForm, canChangeStatus, visibleStatusTransitions]);

  const checkFormValid = (_: unknown, allValues: Record<string, unknown>) => {
    const { driverID, statusName, departmentId } = allValues;
    setIsFormValid(Boolean(driverID || statusName || departmentId));
    if (driverID) setHasDriverChangedInForm(true);
  };

  const [exportData, setExportData] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (data) {
      setExportData(data?.order.map((o) => exportMapper(o)) as any);
    }
  }, [data]);

  return (
    <Spin spinning={isLoading || isFetching || updateOrder.isPending}>
      <Form
        {...layoutFormProps}
        size="small"
        onValuesChange={checkFormValid}
        onFinish={async (v) => {
          try {
            const newStatusName = v.statusName as string | undefined;

            if (newStatusName && requiresDriver(newStatusName)) {
              const hasDriverInUpdate = Boolean(v.driverID);

              if (!hasDriverInUpdate) {
                const ordersWithoutDriver = data?.order?.filter((order) => !order.driver?.id) || [];

                if (ordersWithoutDriver.length > 0) {
                  message.error(
                    'Cannot set orders to this status without assigning a driver. Please select a driver first.'
                  );
                  return;
                }
              }
            }

            for await (const id of orderIds) {
              await updateOrder.mutateAsync({ orderId: id, orderUpdates: v });
            }
            message.success(`${orderIds.length} was updated successfully`);
          } catch (err) {
            message.error(`Failed to update orders. [${String(err)}]`);
          }
        }}
      >
        <div
          style={{ display: 'flex', justifyContent: 'center', gap: '0 10px', position: 'relative' }}
        >
          <h2 style={{ textAlign: 'center' }}>Selected {orderIds.length} orders </h2>

          {Boolean(exportData) && <Export orders={exportData as any} />}

          <Button
            style={{ position: 'absolute', right: 0 }}
            onClick={close}
            shape="circle"
            icon={<CloseOutlined />}
          />
        </div>
        {hasBlockedDriverOrders && !hasDriverChangedInForm && (
          <Alert
            title="Blocked driver detected"
            description="All selected orders have a blocked driver. You can transfer them to Recall status or assign a new driver."
            type="error"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        <FormItem className="app-form-item" label="Driver" name="driverID">
          <Select style={{ width: 200 }} showSearch optionFilterProp="children">
            {data?.user.map((driver) => (
              <Select.Option value={driver.id} key={driver.id}>
                {driver.name}
              </Select.Option>
            ))}
          </Select>
        </FormItem>
        <FormItem className="app-form-item" label="Status" name="statusName">
          <Select style={{ width: 200 }} disabled={!canChangeStatus}>
            <Select.Option
              value={data?.order?.[0]?.status?.name}
              key={data?.order?.[0]?.status?.name}
            >
              {data?.order?.[0]?.status?.name}
            </Select.Option>
            {visibleStatusTransitions.map((transition) => (
              <Select.Option value={transition.next} key={transition.next}>
                {transition.next}
              </Select.Option>
            ))}
            {showRecallOption && (
              <Select.Option value={RECALL_STATUS} key={RECALL_STATUS}>
                {RECALL_STATUS}
              </Select.Option>
            )}
          </Select>
        </FormItem>

        {isManager && (
          <FormItem
            className="app-form-item"
            label="Department"
            name={['departmentId']}
            preserve={false}
          >
            <Select
              style={{ width: 200, display: 'inline-block' }}
              showSearch
              optionFilterProp="children"
            >
              {departments.map((department) => (
                <Option key={department.id} value={department.id}>
                  {department.name}
                </Option>
              ))}
            </Select>
          </FormItem>
        )}

        <FormItem className="app-form-item">
          <Button disabled={!isFormValid} type="primary" htmlType="submit">
            Submit
          </Button>
        </FormItem>
      </Form>
    </Spin>
  );
};

function exportMapper(orderData: Record<string, unknown>): Record<string, unknown> {
  const client = (orderData.client as any) || {};
  const address = (client.address as any) || {};

  const mappedOrder = {
    id: orderData.id,
    totalCents: orderData.totalCents,
    comment: orderData.comment,
    shortCode: orderData.shortCode,
    status: {
      name: (orderData.status as any).name,
      status_transitions: (orderData.status as any).status_transitions,
    },
    createdAt: orderData.createdAt,
    plannedDate: orderData.plannedDate,
    plannedDateDuration: orderData.plannedDateDuration,
    positions:
      (orderData?.positions as any)?.map((position: Record<string, unknown>) => ({
        id: (position.product as any).id, // Map product id to position id for this use case
        product: {
          id: (position.product as any).id,
          name: (position.product as any).name,
        },
        quantity: position.quantity,
        totalPrice: position.totalPrice || 0,
      })) || [],
    client: {
      id: (client as any).id,
      phone: (client as any).phone,
      fullname: (client as any).fullname,
      address: {
        id: (address as any).id,
        city: (address as any).city,
        comments: (address as any).comments,
        country: (address as any).country,
        district: (address as any).district,
        state: (address as any).state,
        street: (address as any).street,
        lat: (address as any).lat,
        lng: (address as any).lng,
      },
      districtId: (client as any).districtId || (address as any).district, // Default to address district if districtId is missing
    },
    driver: orderData.driver
      ? {
          id: (orderData.driver as any).id,
          name: (orderData.driver as any).name,
          email: (orderData.driver as any).email,
        }
      : undefined,
    warehouse: undefined, // Add warehouse details if available in data
    umbrellaID: orderData.umbrellaID,
    reasonRefusal: orderData.reasonRefusal,
    order_photos: orderData.order_photos || [],
    departmentId: orderData.departmentId || undefined,
  };

  return mappedOrder;
}
